import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import DrawerLink from '@/components/Button/DrawerLink';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
import { states } from 'data/static';
import ReviewList from '@/components/Review/ReviewList';
import InstallerHeaderSection from '@/components/StateHeaderSection/InstallerReviewHeaderSection';
import { getCompanyBySlug } from 'data/database';
import { Company } from 'data/types';
import { OrganizationJsonLd } from 'next-seo';
import Link from 'next/link';
import React, { useState } from 'react';
import StateButton from '@/components/StateButton/StateButton';
import Accordion from '@/components/Accordion/Accordion';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import InfoButton from '@/components/Button/InfoButton';
import { YEAR } from 'lib/constants';
import MessageBox from '@/components/MessageBox/MessageBox';

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking', // false or 'blocking'
  };
}

type StaticProps = {
  params: {
    company_slug: string;
  };
};

export async function getStaticProps({
  params: { company_slug },
}: StaticProps) {
  const company = await getCompanyBySlug(company_slug);
  return { props: { company } };
}
type Props = {
  company: Company;
};

const Page = ({ company }: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortFilter, setSortFilter] = useState('highest');

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <div>
      <MetaTags
        title={`${company.name} Solar Installer Reviews`}
        description={`${company.name} reviews and complaints in ${YEAR}. Make an informed decision before going solar with ${company.name}. Get the best deal.`}
      />
      <OrganizationJsonLd
        type="Corporation"
        name={company.name}
        legalName={company.name}
        logo={company.imageURL}
        address={company.HQ}
        url={`https://energypal.com/solar-installer-reviews/${company.slug}`}
      />
      <InstallerHeaderSection
        dark
        installer={company.name}
        rating={company.rating || '0'}
        count={company.reviewCount || '0'}
        imageUrl={company.imageURL || '/images/energypal-icons/e-house-5.png'}
      />
      {/* <SideCardReviewCTA
        FormId={`/solar-installer-reviews/${company.slug}#form`}
      /> */}
      <div className="hidden md:block">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Solar Installer Reviews',
              href: '/solar-installer-reviews',
            },
            {
              name: `${company.name}`,
              href: `/solar-installer-reviews/${company.slug}`,
            },
          ]}
        />
      </div>
      <div className="md:hidden">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Reviews',
              href: '/solar-installer-reviews',
            },
            {
              name: `${company.name}`,
              href: `/solar-installer-reviews/${company.slug}`,
            },
          ]}
        />
      </div>

      <div className="flex md:flex-row flex-col justify-center">
        <div className=" pb-4 md:w-1/5 m-3 md:m-6">
          <div
            className={`bg-white rounded-md shadow-lg h-auto pb-3 ${
              company.HQ ||
              company.statesServiced ||
              (company.inverters &&
                (Array.isArray(company.inverters) ||
                  typeof company.inverters === 'string')) ||
              (company.batteries &&
                (Array.isArray(company.batteries) ||
                  typeof company.batteries === 'string')) ||
              company.workmanshipWarranty ||
              company.equipmentWarranty ||
              company.nabcepCertified ||
              (company.purchasingOptions &&
                (Array.isArray(company.purchasingOptions) ||
                  typeof company.purchasingOptions === 'string')) ||
              company.timeInBusiness
                ? ''
                : 'hidden'
            }`}
          >
            <H4 className="text-xl text-white font-semibold mb-4 px-6 pt-4 pb-2 bg-primary-300 rounded-t-md">
              Company Information
            </H4>
            <dl className="px-6 w-3/4">
              {company.HQ && (
                <div className="mb-2">
                  <dt className="font-semibold">Headquarters:</dt>
                  <dd>{company.HQ}</dd>
                </div>
              )}
              {company.statesServiced && (
                <div className="mb-2">
                  <dt className="font-semibold">States Serviced:</dt>
                  <dd>
                    <Link href={'#statesServed'} legacyBehavior>
                      {company.statesServiced}
                    </Link>
                  </dd>
                </div>
              )}
              {company.inverters && Array.isArray(company.inverters) && (
                <div className="mb-2">
                  <dt className="font-semibold">Inverters Used:</dt>
                  {company.inverters.map((item, index) => (
                    <dd key={index}>{item + ' '}</dd>
                  ))}
                </div>
              )}

              {company.inverters && typeof company.inverters === 'string' && (
                <div className="mb-2">
                  <dt className="font-semibold">Inverters Used:</dt>
                  <dd>{company.inverters}</dd>
                </div>
              )}
              {company.batteries && Array.isArray(company.batteries) && (
                <div className="mb-2">
                  <dt className="font-semibold">Batteries Used:</dt>
                  {company.batteries.map((item, index) => (
                    <dd key={index}>{item + ' '}</dd>
                  ))}
                </div>
              )}
              {company.batteries && typeof company.batteries === 'string' && (
                <div className="mb-2">
                  <dt className="font-semibold">Batteries Used:</dt>
                  <dd>{company.batteries}</dd>
                </div>
              )}
              {company.workmanshipWarranty && (
                <div className="mb-2">
                  <dt className="font-semibold">Workmanship Warranty:</dt>
                  <dd>{company.workmanshipWarranty}</dd>
                </div>
              )}
              {company.equipmentWarranty && (
                <div className="mb-2">
                  <dt className="font-semibold">Equipment Warranty:</dt>
                  <dd>{company.equipmentWarranty}</dd>
                </div>
              )}
              {company.nabcepCertified && (
                <div className="mb-2">
                  <dt className="font-semibold">NABCEP Certified:</dt>
                  <dd>{company.nabcepCertified}</dd>
                </div>
              )}

              {company.purchasingOptions &&
                Array.isArray(company.purchasingOptions) && (
                  <div className="mb-2">
                    <dt className="font-semibold">Purchase Options:</dt>
                    {company.purchasingOptions.map((item, index) => (
                      <dd key={index}>{item + ' '}</dd>
                    ))}
                  </div>
                )}
              {company.purchasingOptions &&
                typeof company.purchasingOptions === 'string' && (
                  <div className="mb-2">
                    <dt className="font-semibold">Batteries Used:</dt>
                    <dd>{company.purchasingOptions}</dd>
                  </div>
                )}

              {/* {company.timeInBusiness && (
                <div className="mb-2">
                  <dt className="font-semibold">Years in Business:</dt>
                  <dd>{company.timeInBusiness}</dd>
                </div>
              )} */}
            </dl>
          </div>
        </div>

        <div className="md:w-1/2 max-w-2xl p-2 pt-6 m-3 md:m-1 md:pl-4 ">
          {company.slug == 'sunpower' && <MessageBox />}

          <div id="reviews">
            <div className="flex flex-col md:flex-row justify-between mt-3 mb-1">
              <div className="flex flex-row">
                <H3 className="mb-4">All Reviews</H3>
                <InfoButton message="Reviews aggregated from multiple sources.¹" />
              </div>
              <div className="mb-4">
                <label htmlFor="sortFilter" className="mr-2">
                  Sort By:
                </label>
                <select
                  id="sortFilter"
                  value={sortFilter}
                  onChange={(e) => setSortFilter(e.target.value)}
                >
                  <option value="highest">Highest</option>
                  <option value="recent">Most Recent</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>
            </div>
            <hr />
            <div className="mt-5">
              <ReviewList
                reviews={
                  company.reviews.length === 0
                    ? company.esReviews
                    : company.reviews
                }
                name={company.name}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                sortFilter={sortFilter}
              />
            </div>
          </div>
        </div>
        <div className="md:w-1/4 hidden md:block"></div>
      </div>

      <CenteredContentSection>
        <div id="statesServed">
          <StatesServedCard states={company.states} />
        </div>
        <H3>What To Look For In A Solar Installer?</H3>
        <p>
          When looking for a solar installer, it&apos;s essential to consider
          various factors to ensure you choose a reputable and competent company
          that meets your needs.
        </p>
        <H4>When selecting an installer like {company.name} consider:</H4>
        <H4>Experience and Reputation:</H4>
        <p>
          Look for a company with a solid track record and years of experience
          in the solar industry. Read the latest {company.name} solar reviews
          from past customers to gauge their reputation.
        </p>
        <H4>Certifications and Licenses:</H4>
        <p>
          Ensure the installer holds the necessary licenses and certifications
          required in your area. Look for certifications from organizations like
          the North American Board of Certified Energy Practitioners (NABCEP) to
          verify their expertise.
        </p>
        <H4>Quality Products and Technology:</H4>
        <p>
          Inquire about the types and brands of solar panels, inverters, and
          other equipment they use. Quality matters for long-term performance.
          Ask if they offer the latest solar technologies, such as
          high-efficiency panels or energy storage solutions.
        </p>
        <p>
          Learn more:{' '}
          <Link href={'/best-solar-panels-for-homes'}>
            Best Solar Panels for Homes
          </Link>{' '}
          |<Link href={'/best-home-battery-systems'}> Top Solar Batteries</Link>
        </p>
        <H4>Warranty Information:</H4>
        <p>
          Understand the warranties they offer for equipment and workmanship. A
          reputable installer should provide comprehensive warranties. The
          majority of modern solar panels are equipped with a warranty spanning
          20 to 30 years, encompassing both the product&apos;s integrity and its
          energy output. An ordinary workmanship warranty offered by a certified
          company typically provides coverage for solar panels for an average
          period of 10 years.
        </p>
        <H4>Transparent Pricing and Contracts:</H4>
        <p>
          Get detailed solar quotes that include all costs, from equipment and
          labor to permits and inspections. Review the contract carefully to
          ensure it&apos;s clear and transparent, with no hidden fees or
          surprises.
        </p>
        <H4>Local Knowledge:</H4>
        <p>
          Choose a company familiar with local building codes, regulations, and
          permitting processes. This can expedite the installation and ensure
          compliance.
        </p>
        <H4>Customized Design:</H4>
        <p>
          Look for companies who provide custom solar system designs tailored to
          your specific energy needs and property characteristics.
        </p>
        <H4>Energy Production Estimates:</H4>
        <p>
          The installer should provide accurate estimates of how much energy the
          system will generate, factoring in local climate conditions and other
          key influences. If your estimated solar savings are much higher with
          one solar company, you&apos;ll want to inquire further to ensure their
          savings projections aren&apos;t skewed to close the sale. Connect with
          us for a <DrawerLink>free estimate.</DrawerLink>
        </p>
        <H4>Financing Options:</H4>
        <p>
          Inquire about{' '}
          <Link href={'/solar-financing-lease-loan'}>
            solar financing options
          </Link>
          , including purchase, lease, or power purchase agreements (PPAs).
          Ensure you know the pros and cons of each option.
        </p>
        <H4>Customer Service:</H4>
        <p>
          Assess their responsiveness and willingness to answer your questions
          and concerns and consider the installer&apos;s commitment to your
          project satisfaction. Throughout your solar process we’ll advocate for
          you, ensuring your solar project is done right.
        </p>
        <H4>References and Recommendations:</H4>
        <p>
          {' '}
          Seek recommendations from friends, family, or colleagues who have
          installed solar panels. Ask the installer for references from past
          clients who can share their experiences. You can also read the{' '}
          <Link href={'#reviews'}>latest reviews</Link> on {company.name}.
        </p>
        <H4>Multiple Quotes:</H4>
        <p>
          Don&apos;t hesitate to obtain quotes from multiple installers to
          compare pricing, services, and warranties. Hint: We can help!
        </p>
        <H4>Financing and Incentive Knowledge:</H4>
        <p>
          Ensure the installer is knowledgeable about available{' '}
          <Link href={'/home-battery-solar-installers-near-me'}>
            incentives, tax credits, rebates
          </Link>{' '}
          and financing options. At EnergyPal, we’ll help maximize your savings.
        </p>
        <H4>Safety Practices:</H4>
        <p>
          Confirm that the installer adheres to safety protocols during
          installation to protect both workers and your property.
        </p>
        <p>
          By researching and considering these factors, you can select a
          reputable solar installer who will provide a quality solar energy
          system and a positive experience throughout the installation process.
          Connect with us to learn more about the{' '}
          <Link href={'/solar-installer-reviews'}>best solar installers</Link>{' '}
          in your area.
        </p>
        <H3>How Do I Compare Solar Installation Options?</H3>
        <p>
          To make an informed decision when comparing solar installation
          options, start by determining your energy needs and seeking multiple
          quotes from reputable installers. Examine the quality of equipment,
          evaluate proposed system designs, and consider financing options,
          factoring in total costs and long-term savings. Assess warranties,
          support, and the installer&apos;s experience while also checking for
          customer feedback and references. Gauge their responsiveness and
          customer service, ensuring they educate you about the process.
          Consider an installer&apos;s licensing, and certifications. Request
          accurate energy production estimates and review contracts for
          transparency. By evaluating these factors, you can confidently choose
          the solar installation option that aligns with your energy goals and
          budget. Connect with us for a <DrawerLink>free quote.</DrawerLink>
        </p>
        <H4>Steps for Installing Solar Panels</H4>

        <ul>
          <li>
            <strong>Qualification:</strong> Contact solar companies through web
            forms, referrals, or flyers. Answer questions to determine
            eligibility.
          </li>
          <li>
            <strong>System Design:</strong> Receive initial solar system design
            in a proposal. Review the design and consider panel placement,
            avoiding &ldquo;under-sizing&ldquo; or &ldquo;oversizing.&ldquo;
          </li>
          <li>
            <strong>Compare Quotes:</strong> Evaluate quotes, considering
            factors like monthly payments, power output, and system details.
            Seek guidance to simplify the comparison.
          </li>
          <li>
            <strong>Contract Signing:</strong> Select a company and panel
            design, understand and sign the contract.
          </li>
          <li>
            <strong>Site Survey:</strong> Technician assesses the site for solar
            panel installation, checking roof orientation and structural
            integrity.
          </li>
          <li>
            <strong>Engineering:</strong> Behind-the-scenes process finalizes
            system layout, electrical configuration, and structural safety for
            permit submission.
          </li>
          <li>
            <strong>Permitting:</strong> The company obtains necessary permits
            and approvals, ensuring compliance with regulations.
          </li>
          <li>
            <strong>Installation:</strong> Expert team mounts panels, installs
            wiring and inverters, connects the system to the grid.
          </li>
          <li>
            <strong>System Activation:</strong> Request final inspections,
            obtain permission to operate (PTO) from the utility, and turn on the
            system. Monitoring apps track power generation. From a signed
            contract to installation and system activation this process can take
            two to six months to complete. Check out our article for a more
            indepth look into{' '}
            <Link
              href={'/solar-faq/how-long-does-it-take-to-install-solar-panels'}
            >
              the solar installation process.
            </Link>
          </li>
        </ul>

        <Accordion
          title="Local Installers, Tax Credits, And Rebates In Your State"
          content={
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {states &&
                states.map((state, index: number) => (
                  <StateButton
                    key={index}
                    image={state.image}
                    state={state.state}
                    link={state.link}
                  />
                ))}
            </div>
          }
        />
        <H4>{company.name} Solar Review Conclusion</H4>
        <p>
          Prior to making a decision to work with {company.name}, homeowners
          should thoroughly review multiple solar installation providers and
          quotes. This ensures they can secure the most suitable choices,
          advantages, and cost-effectiveness that align with their specific
          requirements.
        </p>
        <p>
          Our impartial advisors are here with your best interests in mind. We
          listen, educate, and guide you to ensure you find the energy solution
          that&apos;s the perfect fit for your home.
        </p>
        <p>
          We provide free custom designs tailored precisely to your home&apos;s
          unique requirements, allowing you to compare programs side by side.
        </p>
      </CenteredContentSection>
      <div id="form">
        <FullWidthFormSection cid="ihgm9mfe3sszj4ea" />
      </div>
      <div className="mx-auto">
        <InfoButton message="¹Ratings and reviews are aggrgated from multiple which may include reviews from EnergySage, SolarReviews, Best Company and Consumer Affairs." />
      </div>
    </div>
  );
};

export default Page;

interface StatesServedCardProps {
  states: string[] | string | null | undefined;
}

const StatesServedCard: React.FC<StatesServedCardProps> = ({ states }) => {
  const isArray = Array.isArray(states);
  const isString = typeof states === 'string';

  return (
    <div className="bg-white p-4 pt-0 rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-2 pt-4">States Served</h2>
      {isArray ? (
        <div className="p-2 text-primary">
          {(states as string[]).join(', ')}
        </div>
      ) : isString ? (
        <div className="p-2 text-center text-primary">{states}</div>
      ) : (
        <p className="text-gray-500">No states served information available.</p>
      )}
    </div>
  );
};
